import { StorageKeys } from "@/helpers/enum";
import { ActivityDataModel, ActivityType } from "../models/activitydata.model";
import PostsModel, { PostDomain } from "../models/posts.model";
import UpdatePostModel from "../models/updatepost.model";
import useEmitter from "@/services/broadcast.service";
import httpService from "@/services/http.service";
import storageService from "@/services/storage.service";
import utilService from "@/services/util.service";
import { useTrade } from "@/composables/trade.composable";
import {
  CollectionReference,
  Unsubscribe,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  startAt,
  where,
} from "firebase/firestore";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { TYPE } from "vue-toastification";
import {
  trackEvent,
  trackError,
  startInteraction,
  endInteraction,
} from "@/helpers/tracking.helpers";
import { TrackingEvents } from "@/helpers/enum";
import { useReactionStore } from "@/services/reaction.store";
import { init } from "linkifyjs";
import { useI18n } from "vue-i18n";
import {
  ScrollEventLabels,
  useUIStore,
} from "@/components/notifications-old/stores/use-ui.store";
import RealtimePostCacheManager from "./realtime-post-cache.manager";

const userID = ref<any>();

interface PostsStorage {
  posts: PostsModel[]; // Main posts array
  index: { [postId: string]: number }; // Quick lookup index
  size: number; // Track size for efficiency
}

// Initialize posts storage with arrays and indexes
const postsStorage = ref<PostsStorage>({ posts: [], index: {}, size: 0 });
// const coloniesPostStorage = ref<PostsStorage>({ posts: [], index: {}, size: 0 });
const myPostsStorage = ref<PostsStorage>({ posts: [], index: {}, size: 0 });

const isCommentView = ref<boolean>(true);
const detailPostId = ref<string>("");
const isPostsLoading = ref<boolean>(false);

const listenerRestartAttempts = ref(0);
const maxRestartAttempts = 5;
const isListenerRestarting = ref(false);
const lastListenerError = ref<string | null>(null);

// New notification system for high-volume feeds
const newPostsAvailable = ref(false);

// Track the latest post timestamp we've seen
const currentLatestPostTime = ref<number>(0);
let unSubscribe: Unsubscribe;
const postFetchingDuration = ref<string>("");

let unSubscribeMyPost: Unsubscribe;
let unSubscribeMyTaggedPost: Unsubscribe;

const stockData = ref<any[]>([]);
const selectedAdvanceFilter = ref("tickers");
const userId = ref("");

const advanceFilterSelectedList = ref<any[]>([]);
const loading = ref(false);
const filters = ref([
  { name: "filters.timeline.traderverse", value: "general" },
  { name: "filters.timeline.following", value: "following" },
  { name: "filters.timeline.friends", value: "friends" },
]);
const selectedFilter = ref(filters.value[0].value);

const Advancefilters = ref([
  { name: "filters.timeline.tickers", value: "tickers" },
  { name: "filters.timeline.scope", value: "scope" },
  { name: "filters.timeline.types", value: "types" },
  { name: "filters.timeline.tags", value: "tags" },
]);
const scopelist = ref([
  { name: "filters.timeline.friends", value: "PP_FRIEND", isActive: true },
  { name: "filters.timeline.following", value: "PP_FOLLOWER", isActive: true },
]);

const postTypelist = ref([
  { name: "filters.timeline.text", value: "PT_TEXT", isActive: true },
  { name: "filters.timeline.analysis", value: "PT_ANALYSIS", isActive: true },
  { name: "filters.timeline.portfolio", value: "PT_PORTFOLIO", isActive: true },
  { name: "filters.timeline.profile", value: "PT_PROFILE", isActive: true },
]);

const hashTaglist = ref([
  { name: "filters.timeline.stocks", value: "HT_Stocks", isActive: true },
  { name: "filters.timeline.crypto", value: "HT_Crypto", isActive: true },
  { name: "filters.timeline.nft", value: "HT_NFT", isActive: true },
  { name: "filters.timeline.otc", value: "HT_OTC", isActive: true },
  { name: "filters.timeline.spy", value: "HT_SPY", isActive: true },
]);
const currentUser = ref();
const temPostEding = ref(false);
const isWidgetScrolling = ref(false);
const fromPopup = ref(false);
const isFromHighlight = ref(false);
const noDataFoundonTicker = ref(false);
const noDataFoundOnMyProfile = ref(false);
const currentPage = ref<number>(1);
const currentMyPostsPage = ref<number>(1);
const startAfterSnapshot = ref<any>();
const startAfterMyPostSnapshot = ref<any>();
const startAfterMyTaggedPostSnapshot = ref<any>();
const pageLimit = 30; // default page size
const isMyPostFetching = ref<boolean>(false);

// New notification system
const showNewPostsNotification = ref(false);
const isInitialLoad = ref(false);
const newPostsCount = ref(0);
const pendingNewPosts = ref<PostsModel[]>([]);
let realtimeUnsubscribe: Unsubscribe | null = null;
const lastPostTimestamp = ref<number>(0);

const lastNavigationTime = ref<number>(0);
const navigationThreshold = 100;
const isFreshNavigation = ref(false);

// Helper functions for array-based storage
const storageHelpers = {
  // Add post to storage
  addPost(storage: PostsStorage, post: PostsModel): boolean {
    if (!post._id) return false;

    const existingIndex = storage.index[post._id];
    if (existingIndex !== undefined) {
      // Update existing post
      storage.posts[existingIndex] = post;
      return true;
    } else {
      // Add new post
      const newIndex = storage.posts.length;
      storage.posts.push(post);
      storage.index[post._id] = newIndex;
      storage.size++;
      return true;
    }
  },

  // Get post by ID
  getPost(storage: PostsStorage, postId: string): PostsModel | undefined {
    const index = storage.index[postId];
    return index !== undefined ? storage.posts[index] : undefined;
  },

  // Check if post exists
  hasPost(storage: PostsStorage, postId: string): boolean {
    return storage.index[postId] !== undefined;
  },

  // Remove post by ID
  removePost(storage: PostsStorage, postId: string): boolean {
    const index = storage.index[postId];
    if (index === undefined) return false;

    // Remove from array
    storage.posts.splice(index, 1);

    // Remove from index and update indexes for remaining posts
    delete storage.index[postId];
    for (let i = index; i < storage.posts.length; i++) {
      const currentPostId = storage.posts[i]._id;
      if (currentPostId) {
        storage.index[currentPostId] = i;
      }
    }

    storage.size--;
    return true;
  },

  // Clear all posts
  clear(storage: PostsStorage): void {
    storage.posts = [];
    storage.index = {};
    storage.size = 0;
  },

  // Get all posts (sorted)
  getAllPosts(storage: PostsStorage): PostsModel[] {
    return [...storage.posts].sort(
      (a, b) => (b.modifiedOn || 0) - (a.modifiedOn || 0)
    );
  },

  // Get size
  getSize(storage: PostsStorage): number {
    return storage.size;
  },
};

export const usePosts = () => {
  const emitter = useEmitter();
  const uiStore = useUIStore();
  const { tradeResult, tradeArray, resetTrade } = useTrade();
  const reactionStore = useReactionStore();
  let syncTimeout: ReturnType<typeof setTimeout> | null = null;

  // Convert storage to computed arrays for template compatibility
  const posts = computed(() => {
    return storageHelpers.getAllPosts(postsStorage.value);
  });

  // const coloniesPost = computed(() => storageHelpers.getAllPosts(coloniesPostStorage.value));
  const myPosts = computed(() =>
    storageHelpers.getAllPosts(myPostsStorage.value)
  );

  const dismissNotification = async () => {
    await RealtimePostCacheManager.clearNewPosts();
    showNewPostsNotification.value = false;
    newPostsCount.value = 0;
  };

  // In usePosts composable - replace the watch function
  watch(
    () => reactionStore.reactionCountsVersion,
    (newVersion, oldVersion) => {
      if (newVersion === oldVersion) return;

      // Use a shorter timeout and batch the updates
      if (syncTimeout) {
        clearTimeout(syncTimeout);
      }

      syncTimeout = setTimeout(() => {
        const affectedPostIds = reactionStore.getAndClearAffectedPostIds();

        if (affectedPostIds.size > 0) {
          syncPostsWithReactions(affectedPostIds);
        }
        syncTimeout = null;
      }, 50); // Reduced from 100ms to 50ms
    },
    { flush: "sync" }
  ); // Add flush: 'sync' for immediate execution

  const getDb = () => {
    const db = getFirestore();
    return db;
  };

  const syncPostsWithReactions = (affectedPostIds: Set<string>) => {
    const postsToUpdate: PostsModel[] = [];
    const myPostsToUpdate: PostsModel[] = [];

    // Find affected posts in storage
    affectedPostIds.forEach((postId) => {
      const post = storageHelpers.getPost(postsStorage.value, postId);
      if (post) postsToUpdate.push(post);

      const myPost = storageHelpers.getPost(myPostsStorage.value, postId);
      if (myPost) myPostsToUpdate.push(myPost);
    });

    if (postsToUpdate.length === 0 && myPostsToUpdate.length === 0) {
      return;
    }

    // Use a single batch update for better performance
    const updatePostReactions = (post: PostsModel) => {
      if (!post._id) return;

      try {
        const reaction = reactionStore.getReactionCount(post._id, "post");
        if (reaction) {
          post.updateFromReactionCountModel(reaction);
        }

        const reactionType = reactionStore.getUserReactionType(post._id, "post");
        post.userHasReacted = reactionStore.hasUserReacted(post._id, "post");

        const currentUserId = storageService.getUser()?._id;
        if (currentUserId) {
          post.userReactions =
            post.userReactions?.filter((r) => r.userId !== currentUserId) || [];
          if (reactionType) {
            post.userReactions.push({
              userId: currentUserId,
              reactionType,
            });
          }
        }

        const commentData = reactionStore.getCommentCount(post._id, "post");
        const repostData = reactionStore.getRepostCount(post._id);
        if (commentData?.commentCount !== undefined) {
          post.updateCommentCount(commentData.commentCount);
        }
        if (repostData?.count !== undefined) {
          post.updateRepostCount(repostData.count);
        }
        emitter.emit("postUpdated", post);
      } catch (error) {
        console.error("Error syncing post reactions:", error, {
          postId: post._id,
        });
      }
    };

    // Update all affected posts
    postsToUpdate.forEach(updatePostReactions);
    myPostsToUpdate.forEach(updatePostReactions);
  };

  const { t } = useI18n();

  const checkForFreshNavigation = (): boolean => {
    const currentTime = Date.now();
    const timeSinceLastNavigation = currentTime - lastNavigationTime.value;

    // If it's been more than the threshold since last navigation, consider it fresh
    const isFresh = timeSinceLastNavigation > navigationThreshold;

    if (isFresh) {
      isFreshNavigation.value = true;
      lastNavigationTime.value = currentTime;
    } else {
      isFreshNavigation.value = false;
    }

    return isFresh;
  };

  const addPost = async (addPostDTO: PostsModel) => {
    return new Promise((resolve, reject) => {
      httpService
        .addPost(addPostDTO)
        .then((response) => {
          if (response.data.isSuccess) {
            trackEvent(TrackingEvents.postSharedSuccessfully, {
              postId: response.data.data._id,
              postType: addPostDTO.type,
            });
            let activity = new ActivityDataModel();
            activity.type = ActivityType.post;
            activity.data = response.data.data;
            activity.data.domain = PostDomain.stock;
            resetTrade();
            addPostsToFeed(activity, "added", true);
            utilService.showToast("Post Shared SuccessFully", TYPE.SUCCESS);
            resolve(true);
            trackEvent(TrackingEvents.postSharedSuccessfully, {
              postId: response.data.data._id,
              postType: addPostDTO.type,
            });
          }
          resolve(false);
        })
        .catch((err) => {
          trackError(TrackingEvents.postSharedSuccessfully, {
            postId: addPostDTO._id,
            error: err.message,
          });
          reject(err);
        });
    });
  };

  const updatePost = async (updatePostDTO: UpdatePostModel) => {
    try {
      const response = await httpService.updatePost(updatePostDTO);
      if (response.data.isSuccess) {
        trackEvent(TrackingEvents.postUpdatedSuccess, {
          postId: updatePostDTO.postId,
          action: "update",
        });
        utilService.showToast("Post Updated Successfully.");
      } else {
        trackError(TrackingEvents.postUpdatedSuccess, {
          postId: updatePostDTO.postId,
          reason: response.data.message || "Unknown error",
        });
      }
    } catch (err) {
      trackError(TrackingEvents.postUpdatedSuccess, {
        postId: updatePostDTO.postId,
        error: err.message,
      });
      throw err;
    }
  };

  const deletePost = async (id: string) => {
    try {
      let payload = {
        postId: id,
      };
      const response = await httpService.deletePost(payload);
      if (response.data.isSuccess) {
        trackEvent(TrackingEvents.postDeletedRequestSuccessfully, {
          postId: id,
        });

        // Remove from all storage
        storageHelpers.removePost(postsStorage.value, id);
        storageHelpers.removePost(myPostsStorage.value, id);

        let deletedPostIds = storageService.GetProperty(
          StorageKeys.deletedPosts
        );
        if (!deletedPostIds) {
          deletedPostIds = [];
        }
        deletedPostIds.push(id);
        storageService.SetProperty(StorageKeys.deletedPosts, deletedPostIds);
        utilService.showToast("Post Deleted Successfully.");
        trackEvent(TrackingEvents.postDeletedSuccessfully, { postId: id });
      } else {
        trackError(TrackingEvents.postDeleteFailed, {
          postId: id,
          reason: response.data.message || "Unknown error",
        });
      }
    } catch (err) {
      trackError(TrackingEvents.postDeleteFailed, {
        postId: id,
        error: err.message,
      });
      throw err;
    }
  };

  const fetchPostCount = async (feedId: string, initialDate: number) => {
    return await findTotalPostCountCurrentDay(feedId, initialDate);
  };

  // Modify the getPosts function to update timestamps
  const getPosts = async (page: number, forceRefresh: boolean = false) => {
    const feedId = await getFeedId(true);

    if (isPostsLoading.value) {
      return;
    }

    try {
      isPostsLoading.value = true;

      // Check if this is a fresh navigation (only for page 1)
      const isFreshNav = page === 1 ? checkForFreshNavigation() : false;

      // Initialize first page - ALWAYS clear postsStorage for page 1
      if (page === 1) {
        dismissNotification();
        startAfterSnapshot.value = null;
        storageHelpers.clear(postsStorage.value); // This is CRITICAL - always clear for page 1
        currentPage.value = 0;
        currentPage.value = 1;
        noDataFoundonTicker.value = false;

        // Reset notification state FIRST
        resetNotificationState();

        // Initialize reaction listeners only on first load
      }

      loading.value = true;

      // Get appropriate feed ID
      if (!feedId) {
        return;
      }
      // Build and execute query
      const querySnapshot = await buildAndExecuteQuery(feedId, page);
      isPostsLoading.value = false;
      if (querySnapshot.empty) {
        handleEmptyResults(feedId, page);
      } else {
        handleSuccessfulResults(
          querySnapshot,
          page,
          forceRefresh || isFreshNav,
          feedId
        );
      }
    } catch (error) {
      console.error("Error in getPosts:", error);
    } finally {
      loading.value = false;
    }
  };

  // Helper functions for better organization
  const getFeedId = async (onlyFeedId = false): Promise<string | null> => {
    let feedId = storageService.getUser()?._id;
    // let feedId = "8dcede7c-c3e6-41b8-b6dd-0bd2304d7157";
    if (onlyFeedId) {
      return feedId;
    }
    // Use general feed if user has few recent posts
    if (feedId && advanceFilterSelectedList.value.length === 0) {
      const currentDate = new Date();
      const initialDate = currentDate.setHours(currentDate.getHours() - 24);
      const postCount = await fetchPostCount(feedId, initialDate);

      if (postCount < 10) {
        return "general";
      }
    }

    // Use general feed for certain filter types
    if (
      selectedAdvanceFilter.value !== "scope" &&
      advanceFilterSelectedList.value.length > 0
    ) {
      return "general";
    }

    return feedId || null;
  };

  const buildAndExecuteQuery = async (feedId: string, page: number) => {
    const db = getDb();

    // Build base query
    let baseQuery = query(
      collection(db, "feeds", feedId, "activities"),
      where("type", "==", "post"),
      orderBy("data.modifiedOn", "desc")
    );

    // Apply filters if any
    if (advanceFilterSelectedList.value.length > 0) {
      baseQuery = query(
        baseQuery,
        where(
          "data.filters",
          "array-contains-any",
          advanceFilterSelectedList.value
        )
      );
    }

    // Apply pagination
    let paginatedQuery = baseQuery;
    if (startAfterSnapshot.value) {
      paginatedQuery = query(
        baseQuery,
        limit(pageLimit),
        startAfter(startAfterSnapshot.value)
      );
    } else {
      paginatedQuery = query(baseQuery, limit(pageLimit));
    }

    return getDocs(paginatedQuery);
  };

  const handleEmptyResults = (feedId: string, page: number) => {
    trackEvent(TrackingEvents.postEmptyResult, {
      feedId,
      page,
      filterCount: advanceFilterSelectedList.value.length,
    });
    noDataFoundonTicker.value = true;
  };

  const handleSuccessfulResults = async (
    querySnapshot: any,
    page: number,
    isFreshLoad: boolean = false,
    feedId: any = null
  ) => {
    currentPage.value = page;
    noDataFoundonTicker.value = false;

    // Store the snapshot for next page ONLY if we got results
    if (querySnapshot.docs.length > 0) {
      startAfterSnapshot.value =
        querySnapshot.docs[querySnapshot.docs.length - 1];
    } else {
      console.warn("No documents in querySnapshot to paginate from.");
      utilService.showToast(t("messages.noMorePosts"), TYPE.INFO);
      startAfterSnapshot.value = null;
    }

    // Process posts in batch with animation preparation
    const activities = querySnapshot.docs.map(
      (doc: any) => doc.data() as ActivityDataModel
    );

    // FIX: Use microtask to break reactive chain
    await Promise.resolve();

    // For page 1 with filters, clear existing posts
    if (page === 1) {
      storageHelpers.clear(postsStorage.value);
      uiStore.scrollToTop(ScrollEventLabels.FEED_POSTS);
    }

    // Batch process posts
    const batchPosts: PostsModel[] = [];
    const postIdsNeedingListener: string[] = [];

    activities.forEach((activity) => {
      const post = createPostFromActivity(activity);
      if (post) {
        batchPosts.push(post);
        if (post._id) {
          postIdsNeedingListener.push(post._id);
        }
      }
    });

    // Add all posts in a single batch operation
    if (batchPosts.length > 0) {
      batchPosts.forEach((post) => {
        storageHelpers.addPost(postsStorage.value, post);
      });

      // CRITICAL FIX: Enhanced timestamp calculation
      if (page === 1 && batchPosts.length > 0) {
        const latestPost = batchPosts.reduce((latest, post) =>
          post.modifiedOn > latest.modifiedOn ? post : latest
        );

        // Update timestamp with buffer to avoid detecting initial posts
        currentLatestPostTime.value = latestPost.modifiedOn;
        lastPostTimestamp.value = latestPost.modifiedOn;

        // Set initial load flag and clear any existing notifications
        isInitialLoad.value = true;
        await RealtimePostCacheManager.clearNewPosts();
        showNewPostsNotification.value = false;
        newPostsCount.value = 0;

        setTimeout(() => {
          setupRealtimeListener();
          isInitialLoad.value = false;

          // Allow notifications after ensuring initial posts won't be detected
          // setTimeout(() => {
          //   // console.log("Initial load period ended. Notifications enabled.");
          // }, 3000); // 3 second buffer after listener setup
        }, 1000); // 2 second delay before starting listener
      }
    }

    if (postIdsNeedingListener.length > 0) {
      reactionStore.initReactionListenersForPosts(
        feedId,
        postIdsNeedingListener
      );
    }

    utilService.updateReadCount();
  };

  const createPostFromActivity = (
    activity: ActivityDataModel
  ): PostsModel | null => {
    try {
      let tempPost = new PostsModel();
      Object.assign(tempPost, activity.data);
      tempPost._id = activity.data._id;
      tempPost.domain = PostDomain.stock;
      tempPost.color = tempPost.getColor();

      if (tempPost.prediction) {
        tempPost.prediction.showChart = false;
      }

      tempPost.relations = activity.data.relations;

      if (typeof activity.data?.chart?.data == "string") {
        tempPost.chart!.data = JSON.parse(activity.data.chart.data);
      }

      // Check if post is deleted
      let deletedIds: string[] = storageService.GetProperty(
        StorageKeys.deletedPosts
      );
      if (deletedIds && deletedIds.includes(tempPost._id!)) {
        return null;
      }

      tempPost.initializeReactionsFromEmbeddedData();

      return tempPost;
    } catch (error) {
      console.error("Error creating post from activity:", error);
      return null;
    }
  };

  const resetNotificationState = () => {
    showNewPostsNotification.value = false;
    newPostsAvailable.value = false;
    newPostsCount.value = 0;
    pendingNewPosts.value = [];
  };

  const setupRealtimeListener = async () => {
    // console.log("Setting Up Realtime Listener with timestamp:", currentLatestPostTime.value);

    // Always clean up existing listener first to avoid duplicates
    if (realtimeUnsubscribe) {
      stopRealtimeListener();
    }

    // Reset restart attempts if we're successfully setting up
    listenerRestartAttempts.value = 0;
    isListenerRestarting.value = false;
    lastListenerError.value = null;

    const feedId = await getFeedId(true);
    const currentUserId = storageService.getUser()?._id;

    if (!feedId) {
      lastListenerError.value = "No feed ID found";
      return;
    }

    try {
      const db = getDb();

      // CRITICAL FIX: Use a small buffer time to avoid duplicate detection
      const bufferTime = 5000; // 5 second buffer
      const effectiveTimestamp =
        currentLatestPostTime.value > 0
          ? currentLatestPostTime.value
          : Date.now() - bufferTime; // If no posts yet, use current time minus buffer

      // console.log("Effective timestamp for listener:", effectiveTimestamp);

      const baseQuery = query(
        collection(db, "feeds", feedId, "activities"),
        where("type", "==", "post"),
        where("data.modifiedOn", ">", effectiveTimestamp),
        orderBy("data.modifiedOn", "desc"),
        limit(50)
      );

      // In setupRealtimeListener function, update the snapshot handler:
      realtimeUnsubscribe = onSnapshot(
        baseQuery,
        {
          includeMetadataChanges: false,
        },
        (querySnapshot) => {
          // Reset error state on successful snapshot
          lastListenerError.value = null;
          listenerRestartAttempts.value = 0;

          if (querySnapshot.empty) {
            return;
          }

          let validNewPostsCount = 0;
          const now = Date.now();

          querySnapshot.docChanges().forEach((change) => {
            const activityData = change.doc.data() as ActivityDataModel;
            const postData = activityData.data;
            const postId = postData._id;

            // CRITICAL: Additional duplicate prevention checks
            const isDuplicate = storageHelpers.hasPost(
              postsStorage.value,
              postId
            );
            const isCurrentUserPost = postData.userId === currentUserId;
            const isFromInitialLoadPeriod =
              isInitialLoad.value && now - postData.modifiedOn < 10000; // 10 second window

            // NEW: Check if post matches current filters
            const matchesFilters = checkIfPostMatchesFilters(activityData.data);

            if (change.type === "removed") {
              storageHelpers.removePost(postsStorage.value, postId);
              storageHelpers.removePost(myPostsStorage.value, postId);
            } else if (change.type === "added" || change.type === "modified") {
              // STRICTER CONDITIONS FOR NEW POST NOTIFICATIONS
              const shouldNotifyAsNew =
                !isDuplicate &&
                !isCurrentUserPost &&
                !isFromInitialLoadPeriod &&
                postData.modifiedOn > effectiveTimestamp &&
                matchesFilters; // ADDED FILTER CHECK

              if (shouldNotifyAsNew) {
                const tempPost = new PostsModel();
                Object.assign(tempPost, postData);
                tempPost._id = postId;

                // Store the post for later loading
                RealtimePostCacheManager.addNewPost(tempPost);
                validNewPostsCount++;
              } else if (isDuplicate && change.type === "modified") {
                // Update existing post without notification
                const existingPost = storageHelpers.getPost(
                  postsStorage.value,
                  postId
                );
                if (existingPost) {
                  const updatedPost = new PostsModel();
                  Object.assign(updatedPost, postData);
                  updatedPost._id = postId;

                  // Prefer embedded reaction data from incoming post; fallback to existing
                  if (!postData.reactionCounts) {
                    updatedPost.reactionCounts = {
                      ...existingPost.reactionCounts,
                    };
                    updatedPost.totalReactions = existingPost.totalReactions;
                    updatedPost.commentCount = existingPost.commentCount;
                    updatedPost.repostCount = existingPost.repostCount;
                    updatedPost.userReactions = [
                      ...(existingPost.userReactions || []),
                    ];
                    updatedPost.userHasReacted = existingPost.userHasReacted;
                  } else {
                    updatedPost.initializeReactionsFromEmbeddedData();
                  }

                  storageHelpers.addPost(postsStorage.value, updatedPost);
                  emitter.emit("post", updatedPost);
                }
              }
            }
          });

          // Update new posts count and show notification
          RealtimePostCacheManager.getNewPostsCount().then((count) => {
            newPostsCount.value = count;

            // Only show notification if we have genuinely new posts AND initial load is complete
            if (count > 0 && !isInitialLoad.value && validNewPostsCount > 0) {
              showNewPostsNotification.value = true;
              newPostsCount.value = Math.min(newPostsCount.value, 99);
              newPostsAvailable.value = true;

              trackEvent(TrackingEvents.newPostsDetected, {
                count: newPostsCount.value,
                validNewPostsCount,
                currentPostsCount: storageHelpers.getSize(postsStorage.value),
                effectiveTimestamp,
                hasActiveFilters: advanceFilterSelectedList.value.length > 0,
              });
            } else if (count === 0) {
              // Clear notification if no new posts
              showNewPostsNotification.value = false;
              newPostsAvailable.value = false;
            }
          });
        },
        (error) => {
          console.log("Realtime listener error:", error);
        }
      );
    } catch (error) {
      console.error("Error setting up realtime listener:", error);
      lastListenerError.value = error.message;
    }
  };

  // Helper function to check if component is on feed route
  const isComponentOnFeedRoute = (): boolean => {
    if (typeof window !== "undefined") {
      const currentPath = window.location.pathname;
      return currentPath.includes("/profile/feed");
    }
    return false;
  };

  const checkRealtimeListenerStatus = () => {
    return {
      isActive: !!realtimeUnsubscribe,
      feedId: storageService.getUser()?._id,
      postsCount: storageHelpers.getSize(postsStorage.value),
      lastPostTime: currentLatestPostTime.value,
    };
  };

  const restartRealtimeListener = () => {
    listenerRestartAttempts.value = 0;
    lastListenerError.value = null;
    stopRealtimeListener();

    setTimeout(() => {
      setupRealtimeListener();
    }, 500);
  };

  const loadNewPosts = async () => {
    if (!newPostsAvailable.value) return;

    trackEvent(TrackingEvents.newPostsRefreshStarted, {
      currentPostCount: storageHelpers.getSize(postsStorage.value),
      filterCount: advanceFilterSelectedList.value.length,
    });

    try {
      loading.value = true;
      showNewPostsNotification.value = false;
      newPostsAvailable.value = false;

      const cachedNewPosts = await RealtimePostCacheManager.getAllNewPosts();

      // CRITICAL: Filter based on active advanceFilterSelectedList
      const trulyNewPosts = cachedNewPosts.filter((post) => {
        // 1. Check for duplicates
        const isDuplicate = storageHelpers.hasPost(
          postsStorage.value,
          post._id!
        );

        // 2. Check if post is from current user
        const isFromCurrentUser = post.userId === storageService.getUser()?._id;

        // 3. Check if post matches current filters (if any filters are active)
        const matchesFilters = checkIfPostMatchesFilters(post);

        if (!isDuplicate && !isFromCurrentUser && matchesFilters) {
          // console.log("Loading truly new post (matches filters):", post._id);
          return true;
        } else {
          // console.log("Filtering out post:", post._id, {
          //   isDuplicate,
          //   isFromCurrentUser,
          //   matchesFilters
          // });
          return false;
        }
      });

      if (trulyNewPosts.length === 0) {
        await RealtimePostCacheManager.clearNewPosts();
        // console.log("No truly new posts to load (after filter check)");
        return;
      }

      await RealtimePostCacheManager.clearNewPosts();

      // Add only the truly new posts
      trulyNewPosts.forEach((post) => {
        const activityData = { type: "post", data: post } as ActivityDataModel;
        addPostsToFeed(activityData, "added", true);
      });

      // CRITICAL: Update the latest post timestamp to include newly loaded posts
      if (trulyNewPosts.length > 0) {
        const latestNewPost = trulyNewPosts.reduce((latest, post) =>
          post.modifiedOn > latest.modifiedOn ? post : latest
        );

        // Only update if the new post is actually newer
        if (latestNewPost.modifiedOn > currentLatestPostTime.value) {
          currentLatestPostTime.value = latestNewPost.modifiedOn;
          // console.log("Updated latest post timestamp to:", currentLatestPostTime.value);
        }
      }

      trackEvent(TrackingEvents.newPostsRefreshed, {
        newPostsCount: trulyNewPosts.length,
        totalPostCount: storageHelpers.getSize(postsStorage.value),
        filteredCount: cachedNewPosts.length - trulyNewPosts.length,
      });
    } catch (error) {
      trackError(TrackingEvents.newPostsRefreshError, {
        error: error.message,
      });
    } finally {
      loading.value = false;
    }
  };

  // Helper function to check if post matches current filters
  const checkIfPostMatchesFilters = (post: PostsModel): boolean => {
    // If no filters are active, all posts match
    if (
      !advanceFilterSelectedList.value ||
      advanceFilterSelectedList.value.length === 0
    ) {
      return true;
    }

    // Check if post has filters property
    if (!post.filters || !Array.isArray(post.filters)) {
      return false;
    }

    // Check if any of the post's filters match any of the selected filters
    const postFilters = post.filters;
    const hasMatchingFilter = advanceFilterSelectedList.value.some(
      (selectedFilter) => postFilters.includes(selectedFilter)
    );

    return hasMatchingFilter;
  };

  // Stop realtime listener
  const stopRealtimeListener = () => {
    if (realtimeUnsubscribe) {
      realtimeUnsubscribe();
      realtimeUnsubscribe = null;
      isListenerRestarting.value = false;
    }
  };

  // FIXED: Improved processPostsBatch with better tracking
  // const processPostsBatch = (
  //   activities: ActivityDataModel[],
  //   updateType: string,
  //   atFirst: boolean
  // ) => {
  //   let addedCount = 0;
  //   let skippedCount = 0;

  //   activities.forEach(activity => {
  //     const wasAdded = addPostsToFeed(activity, updateType, atFirst);
  //     if (wasAdded) {
  //       addedCount++;
  //     } else {
  //       skippedCount++;
  //     }
  //   });
  // };

  const findTotalPostCountCurrentDay = async (
    feedId: string,
    initialDate: number
  ) => {
    try {
      const db = getDb();
      const feedRef = collection(db, "feeds", feedId, "activities");
      const q1 = query(feedRef);
      const docs = await getDocs(q1);

      return docs.docs.length;
    } catch (e) {
      return storageHelpers.getSize(postsStorage.value);
    }
  };

  // In your posts.composables.ts - replace the clear method
  const clearPosts = () => {
    if (unSubscribe) {
      unSubscribe();
    }
    if (unSubscribeMyPost) {
      unSubscribeMyPost();
    }
    if (unSubscribeMyTaggedPost) {
      unSubscribeMyTaggedPost();
    }

    // Stop realtime listener
    stopRealtimeListener();

    // Use store cleanup instead of local reaction cleanup
    reactionStore.cleanupReactionListeners();

    // FIX: Use setTimeout to break the reactive chain
    setTimeout(() => {
      storageHelpers.clear(postsStorage.value);
      storageHelpers.clear(myPostsStorage.value);
    }, 0);

    // Reset notification state
    setTimeout(() => {
      showNewPostsNotification.value = false;
      newPostsCount.value = 0;
      pendingNewPosts.value = [];
    }, 0);
  };

  // FIXED: Modified addPostsToFeed to return boolean indicating if post was added
  const addPostsToFeed = (
    activityData: ActivityDataModel,
    firebaseUpdateType: string,
    atFirst: boolean = false
  ): boolean => {
    let tempPost = new PostsModel();
    Object.assign(tempPost, activityData.data);
    tempPost._id = activityData.data._id;
    tempPost.domain = PostDomain.stock;
    tempPost.color = tempPost.getColor();

    if (tempPost.prediction) {
      tempPost.prediction.showChart = false;
    }

    tempPost.relations = activityData.data.relations;

    if (typeof activityData.data?.chart?.data == "string") {
      tempPost.chart!.data = JSON.parse(activityData.data.chart.data);
    }

    let deletedIds: string[] = storageService.GetProperty(
      StorageKeys.deletedPosts
    );
    let deletedIndex = deletedIds ? deletedIds.indexOf(tempPost._id!) : -1;

    if (deletedIndex == -1) {
      tempPost.initializeReactionsFromEmbeddedData();

      // Use storageHelpers for better performance
      if (firebaseUpdateType === "added") {
        if (!storageHelpers.hasPost(postsStorage.value, tempPost._id!)) {
          storageHelpers.addPost(postsStorage.value, tempPost);
          return true; // Post was added
        }
      } else if (firebaseUpdateType === "modified") {
        if (storageHelpers.hasPost(postsStorage.value, tempPost._id!)) {
          const existingPost = storageHelpers.getPost(
            postsStorage.value,
            tempPost._id!
          );

          // Prefer embedded reaction data from incoming post; fallback to existing
          if (!activityData.data.reactionCounts && existingPost) {
            tempPost.reactionCounts = { ...existingPost.reactionCounts };
            tempPost.totalReactions = existingPost.totalReactions;
            tempPost.commentCount = existingPost.commentCount;
            tempPost.repostCount = existingPost.repostCount;
            tempPost.userReactions = [...(existingPost.userReactions || [])];
            tempPost.userHasReacted = existingPost.userHasReacted;
          }

          storageHelpers.addPost(postsStorage.value, tempPost);
          emitter.emit("post", tempPost);
          return true;
        }
      } else if (firebaseUpdateType === "removed") {
        storageHelpers.removePost(postsStorage.value, tempPost._id!);
        storageHelpers.removePost(myPostsStorage.value, tempPost._id!);
      }
    }

    return false; // Post was not added
  };

  const setUserId = (id: string) => {
    userId.value = id;
  };
  const setUserID = (id: string) => {
    userID.value = id;
  };

  const getPostById = async (id: string) => {
    try {
      if (id) {
        // Check in storage first for O(1) lookup
        var post =
          storageHelpers.getPost(postsStorage.value, id) ||
          storageHelpers.getPost(myPostsStorage.value, id);

        if (!post) {
          try {
            await httpService.getPublicPostById(id).then((res) => {
              if (res.isSuccess) {
                let obj = new PostsModel();
                obj = Object.assign(obj, res.data);
                post = obj;
              }
            });
          } catch (e) {}
        }
        if (!post) {
          const db = getFirestore();
          const docRef = doc(db, "feeds", "general", "activities", id);
          const docSnap = await getDoc(docRef);
          let obj = new PostsModel();
          obj = Object.assign(obj, docSnap.data()!.data);
          post = obj;
        }
        post?.initializeReactionsFromEmbeddedData();
        return post;
      }
    } catch (e) {
      // utilService.showToast("Post not Found", TYPE.ERROR);
    }
    return;
  };

  const voteOnPollChart = async (data: any) => {
    return await reactionStore.voteOnPoll(data);
  };

  const getPollCountByPost = (postId: string) => {
    return reactionStore.getPollCount(postId);
  };

  const getMyPosts = async (userId: string, page: number) => {
    try {
      if (page === 1) {
        postFetchingDuration.value = new Date().toString();
        startAfterMyPostSnapshot.value = null;
        startAfterMyTaggedPostSnapshot.value = null;
        noDataFoundOnMyProfile.value = false;
        storageHelpers.clear(myPostsStorage.value);
      }
      if (noDataFoundOnMyProfile.value) {
        return;
      }
      isMyPostFetching.value = true;
      const db = getDb();
      let q: any;
      const myProfileQuery = query(
        collection(db, "feeds", userId!, "activities"),
        orderBy("data.modifiedOn", "desc")
      );
      let userPostsQuery = query(
        myProfileQuery,
        where("data.userId", "==", userId)
      );

      if (
        advanceFilterSelectedList.value &&
        advanceFilterSelectedList.value.length
      ) {
        const filterQuery = where(
          "data.filters",
          "array-contains-any",
          advanceFilterSelectedList.value
        );
        userPostsQuery = query(userPostsQuery, filterQuery);
      }

      let paginatedQuery: any;
      if (startAfterMyPostSnapshot.value) {
        paginatedQuery = query(
          userPostsQuery,
          limit(pageLimit),
          startAfter(startAfterMyPostSnapshot.value)
        );
      } else {
        paginatedQuery = query(userPostsQuery, limit(pageLimit));
      }

      let userPostsSnapshot;
      try {
        userPostsSnapshot = await getDocs(paginatedQuery);
      } catch (e) {
        isMyPostFetching.value = false;
        return;
      }

      if (!userPostsSnapshot.empty) {
        startAfterMyPostSnapshot.value =
          userPostsSnapshot.docs[userPostsSnapshot.docs.length - 1];
        userPostsSnapshot.docs.forEach((doc: any) => {
          const activityData: ActivityDataModel =
            doc.data() as ActivityDataModel;
          if (activityData.type === "post") {
            addPostsToMyFeed(activityData);
          }
        });
        const myPostIds = storageHelpers
          .getAllPosts(myPostsStorage.value)
          .map((p) => p._id)
          .filter(Boolean) as string[];
        if (myPostIds.length > 0) {
          reactionStore.initReactionListenersForPosts(userId, myPostIds);
        }
      } else {
        noDataFoundOnMyProfile.value = true;
      }

      // Set loading to false AFTER posts are processed and added
      isMyPostFetching.value = false;
    } catch (e) {
      isMyPostFetching.value = false;
    }
  };

  const addPostsToMyFeed = (activityData: ActivityDataModel) => {
    let tempPost = new PostsModel();
    Object.assign(tempPost, activityData.data);
    tempPost._id = activityData.data._id;
    tempPost.domain = PostDomain.stock;
    tempPost.color = tempPost.getColor();
    if (typeof activityData.data?.chart?.data == "string") {
      tempPost.chart!.data = JSON.parse(activityData.data.chart.data);
    }
    let deletedIds: string[] = storageService.GetProperty(
      StorageKeys.deletedPosts
    );
    let deletedIndex = -1;
    if (deletedIds) {
      deletedIndex = deletedIds.indexOf(tempPost._id!);
    }
    if (deletedIndex == -1) {
      tempPost.initializeReactionsFromEmbeddedData();
      if (!storageHelpers.hasPost(myPostsStorage.value, tempPost._id!)) {
        storageHelpers.addPost(myPostsStorage.value, tempPost);
      }
    }
  };

  const getStocks = async () => {
    httpService
      .getTopNasdaqStocks()
      .then((res) => {
        if (res.isSuccess) {
          stockData.value = res.data;
        }
      })
      .catch((e) => {
        // utilService.showToast("Error Getting Stocks", TYPE.ERROR);
      });
  };

  const checkIfItemAlreadySelected = (data: any) => {
    return advanceFilterSelectedList.value.findIndex((x) => x == data) != -1;
  };
  const clearFilter = async () => {
    if (
      advanceFilterSelectedList.value &&
      advanceFilterSelectedList.value.length
    ) {
      loading.value = true;
      advanceFilterSelectedList.value = [];
      storageService.SetProperty(TIMELINE_FILTERS_STORAGE_KEY, []);

      // CRITICAL FIX: Reset pagination state when clearing filters
      startAfterSnapshot.value = null;
      // currentPage.value = 1;

      selectedAdvanceFilter.value = "tickers";

      // RESET NOTIFICATION STATE
      showNewPostsNotification.value = false;
      newPostsAvailable.value = false;
      newPostsCount.value = 0;

      getPosts(1);
      loading.value = false;
      if (userID.value) {
        applyFilter();
      }
    }
  };

  const documentLoaded = ref(false);
  const LOCAL_STORAGE_KEY = "documentLoaded";
  const TIMELINE_FILTERS_STORAGE_KEY = "timelineFilters";
  const handleWindowLoad = () => {
    documentLoaded.value = true;
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
  };
  onMounted(() => {
    const savedLoadedState = localStorage.getItem(LOCAL_STORAGE_KEY);
    const timelineFilters = JSON.parse(
      localStorage.getItem(TIMELINE_FILTERS_STORAGE_KEY) || "[]"
    );
    if (timelineFilters?.length) {
      advanceFilterSelectedList.value = timelineFilters;
    } else {
      advanceFilterSelectedList.value = [];
    }
    if (savedLoadedState === "true") {
      documentLoaded.value = true;
    }
    window.addEventListener("load", handleWindowLoad);
    setTimeout(() => {
      documentLoaded.value = true;
    }, 6000);
  });
  onBeforeUnmount(() => {
    window.removeEventListener("load", handleWindowLoad);
    stopRealtimeListener();
  });
  window.addEventListener("beforeunload", () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  });
  const advanceFilterAddAndRemove = async (data: any) => {
    if (!documentLoaded.value) return;
    if (
      advanceFilterSelectedList.value &&
      advanceFilterSelectedList.value.length
    ) {
      if (advanceFilterSelectedList.value.includes(data)) {
        let info = advanceFilterSelectedList.value.findIndex((x) => x === data);
        if (info !== -1) {
          advanceFilterSelectedList.value.splice(info, 1);
          applyFilter();
        }
      } else {
        if (advanceFilterSelectedList.value.length < 30) {
          advanceFilterSelectedList.value.push(data);
          applyFilter();
        } else {
          return utilService.showToast(
            "Cannot Select More Filters!",
            TYPE.ERROR
          );
        }
      }
    } else {
      advanceFilterSelectedList.value.push(data);
      applyFilter();
    }
    storageService.SetProperty(
      TIMELINE_FILTERS_STORAGE_KEY,
      advanceFilterSelectedList.value,
      true
    );
  };

  const advanceFiltering = async (data: any) => {
    if (!documentLoaded.value) return;
    if (
      advanceFilterSelectedList.value &&
      advanceFilterSelectedList.value.length
    ) {
      if (advanceFilterSelectedList.value.includes(data)) {
      } else {
        if (advanceFilterSelectedList.value.length < 30) {
          advanceFilterSelectedList.value.push(data);
          applyFilter();
        } else {
          return utilService.showToast(
            "Cannot Select More Filters!",
            TYPE.ERROR
          );
        }
      }
    } else {
      advanceFilterSelectedList.value.push(data);
      applyFilter();
    }
    storageService.SetProperty(
      TIMELINE_FILTERS_STORAGE_KEY,
      advanceFilterSelectedList.value,
      true
    );
  };

  const applyFilter = async () => {
    loading.value = true;

    // CRITICAL: Always clear everything when filters change
    storageHelpers.clear(postsStorage.value);
    storageHelpers.clear(myPostsStorage.value);
    startAfterSnapshot.value = null;
    currentPage.value = 1;
    noDataFoundonTicker.value = false;
    await RealtimePostCacheManager.clearNewPosts();
    // RESET NOTIFICATION STATE WHEN FILTERS CHANGE
    showNewPostsNotification.value = false;
    newPostsAvailable.value = false;
    newPostsCount.value = 0;
    currentLatestPostTime.value = 0;
    lastPostTimestamp.value = 0;

    if (userID.value) {
      getMyPosts(userID.value, 1);
    } else {
      getPosts(1); // This will trigger cache clearing in the enhanced version
    }
    loading.value = false;

    // CRITICAL: Re-initialize infinite scroll after filter change
    setTimeout(() => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("filters-changed"));
      }
    }, 500);
  };

  const selectAdvanceFilter = (value: any) => {
    selectedAdvanceFilter.value = value;
  };

  const getRealtimeListenerStatus = () => {
    const status = {
      isActive: !!realtimeUnsubscribe,
      isRestarting: isListenerRestarting.value,
      restartAttempts: listenerRestartAttempts.value,
      maxRestartAttempts: maxRestartAttempts,
      lastError: lastListenerError.value,
      feedId: storageService.getUser()?._id,
      postsCount: storageHelpers.getSize(postsStorage.value),
      lastPostTime: currentLatestPostTime.value,
      onFeedRoute: isComponentOnFeedRoute(),
    };

    return status;
  };

  const updateLastNavigationTime = () => {
    lastNavigationTime.value = Date.now();
  };

  const forcePostsReRender = async (): Promise<void> => {
    if (storageHelpers.getSize(postsStorage.value) === 0) return;

    // console.log('🔄 Forcing posts re-render for column redistribution');

    try {
      // Store current posts
      const currentPosts = [...storageHelpers.getAllPosts(postsStorage.value)];

      // Clear storage to trigger re-render
      storageHelpers.clear(postsStorage.value);

      // Wait for Vue to react to the change
      await new Promise((resolve) => setTimeout(resolve, 0));

      // Restore posts - this will trigger columnPosts recomputation
      currentPosts.forEach((post) => {
        storageHelpers.addPost(postsStorage.value, post);
      });

      // console.log(`✅ Successfully re-rendered ${currentPosts.length} posts`);
    } catch (error) {
      console.error("❌ Error forcing posts re-render:", error);
      throw error;
    }
  };

  // Method to force redistribution when column count changes
  const forcePostsRedistribution = async (): Promise<void> => {
    if (storageHelpers.getSize(postsStorage.value) === 0) return;

    // console.log('🔄 Forcing posts redistribution');

    try {
      // Use the re-render method
      await forcePostsReRender();

      // Force reaction store sync after redistribution
      setTimeout(() => {
        const affectedPostIds = new Set(
          storageHelpers
            .getAllPosts(postsStorage.value)
            .map((post) => post._id!)
        );
        syncPostsWithReactions(affectedPostIds);
      }, 50);
    } catch (error) {
      console.error("❌ Error forcing posts redistribution:", error);
      throw error;
    }
  };

  // Method to clear posts storage (for external use)
  const clearPostsStorage = (): void => {
    storageHelpers.clear(postsStorage.value);
  };

  // Method to get posts count
  const getPostsCount = (): number => {
    return storageHelpers.getSize(postsStorage.value);
  };

  // Method to check if posts storage is empty
  const isPostsEmpty = (): boolean => {
    return storageHelpers.getSize(postsStorage.value) === 0;
  };

  return {
    advanceFiltering,
    TIMELINE_FILTERS_STORAGE_KEY,
    documentLoaded,
    isFromHighlight,
    hashTaglist,
    applyFilter,
    postTypelist,
    scopelist,
    stockData,
    checkIfItemAlreadySelected,
    getStocks,
    advanceFilterAddAndRemove,
    clearFilter,
    selectedAdvanceFilter,
    filters,
    selectedFilter,
    isMyPostFetching,
    getPosts,
    selectAdvanceFilter,
    clearPosts,
    setUserId,
    setUserID,
    addPost,
    updatePost,
    voteOnPollChart,
    posts, // computed array for template compatibility
    detailPostId,
    deletePost,
    isCommentView,
    getPostById,
    userID,
    getMyPosts,
    isWidgetScrolling,
    myPosts, // computed array for template compatibility
    loading,
    currentUser,
    temPostEding,
    fromPopup,
    noDataFoundonTicker,
    currentPage,
    currentMyPostsPage,
    advanceFilterSelectedList,
    Advancefilters,
    getPollCountByPost,
    commentCount: computed(() => {
      return Array.from(reactionStore.commentCounts.values());
    }),
    // New notification system exports
    showNewPostsNotification: computed(() => showNewPostsNotification.value),
    dismissNotification,
    newPostsCount: computed(() => newPostsCount.value),
    setupRealtimeListener,
    stopRealtimeListener,
    loadNewPosts,
    checkRealtimeListenerStatus,
    restartRealtimeListener,
    postFetchingDuration,
    getRealtimeListenerStatus,
    isComponentOnFeedRoute,
    maxRestartAttempts,
    updateLastNavigationTime,
    checkForFreshNavigation,
    forcePostsRedistribution,
    forcePostsReRender,
    clearPostsStorage,
    getPostsCount,
    isPostsEmpty,

    // postsMap // Backward compatibility
  };
};
